<template>
  <q-page class="column">
    <q-tabs
      v-model="selectedTab"
      inverted
      class="q-pb-lg text-primary full-width bg-white"
      v-show="showTabs"
    >
      <q-tab
        name="myScope"
        icon="person_pin_circle"
        :label="$t('common.myScope')"
      />
      <q-tab
        name="other"
        icon="add_circle_outline"
        :label="$t('common.other')"
      />
    </q-tabs>
    <audits-list
      v-if="selectedTab === 'myScope'"
      :zones="zones"
      :loadingAudits="isLoadingAudits || isReducingZones"
      :class="showTabs ? 'root' : 'rootWithoutTabs'"
    />
    <div
      v-else
      class="q-px-md"
    >
      <!-- TopZone -->
      <q-field borderless v-show="agencies && agencies.length > 1">
        <template v-slot:before>
          <q-icon name="store_mall_directory" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedAgency"
              label="Agence"
              :options="agencySelectOptions"
            />
          </div>
        </template>
      </q-field>
      <!-- Zone -->
      <q-field borderless v-show="selectedAgencyId">
        <template v-slot:before>
          <q-icon name="location_city" color="primary" />
        </template>
        <template v-slot:control>
          <div class="self-center full-width no-outline" tabindex="0">
            <sw-filterable-select
              v-model="selectedResidence"
              :label="$t('issueDetails.residence')"
              :options="residenceSelectOptions"
            />
          </div>
        </template>
      </q-field>
      <!-- Place -->
      <q-list class="q-ma-md" v-show="selectedResidenceId">
        <q-item
          clickable
          :disable="loadingAudits"
          v-ripple
          v-for="place in sortedPlaces"
          :key="place.id"
          @click="loadAuditsInOtherScopes(place)"
        >
          <q-item-section avatar>
            <q-btn
              round
              color="primary"
              size="15px"
              :key="'btn' + place.id"
              class="float-right"
            >
              <q-icon name="place" size="30px" />
            </q-btn>
          </q-item-section>
          <q-item-section class="uppercase text-black">{{
            place.name
          }}</q-item-section>
          <q-item-section avatar v-if="loadingAudits && place.id == selectedId">
            <q-spinner-ios class="sw-c-loader" color="primary" size="sm" />
          </q-item-section>
        </q-item>
      </q-list>
    </div>
  </q-page>
</template>
<script>
import { mapGetters } from 'vuex'
import AuditsList from 'src/components/audits/List'
import SwFilterableSelect from 'src/components/inputs/sw-filterable-select.vue'
import { alphabeticalSort } from 'src/utils/sort'

export default {
  inject: ['$helpers'],
  components: {
    AuditsList,
    SwFilterableSelect
  },
  data() {
    return {
      selectedTab: 'myScope',
      selectedId: '',
      agencies: [],
      residences: [],
      places: [],
      selectedAgency: { label: '', value: '' },
      selectedResidence: { label: '', value: '' },
      selectedPlaceId: null,
      agencySelectOptions: [],
      residenceSelectOptions: [],
      zones: [],
      isReducingZones: false
    }
  },
  beforeMount() {
    let selectedTab = ''
    this.isChecklister()
      ? (selectedTab = this.$route.query.tab
          ? this.$route.query.tab
          : 'myScope')
      : (selectedTab = 'other')
    this.selectedTab = selectedTab
  },
  async mounted() {
    await this.$store.dispatch('audits/reload')
    this.zones = await this.reducedZones()
    this.$q.loading.hide()
  },
  watch: {
    selectedTab() {
      this.resetAuditsInOtherScopes()
      if (this.selectedTab === 'other') {
        this.$q.loading.show()
        this.$SowellProxy
          .loadCompanyAgencies(this.company.id)
          .then((response) => {
            this.agencies = response.data.map((agency) => {
              return {
                id: agency.id,
                name: agency.name
              }
            })
            this.agencySelectOptions = this.sortList(
              this.$helpers.resourcesToFormOptions(this.agencies)
            )
            if (this.currentOtherControl?.agency?.id) {
              this.selectAgency({
                name: this.currentOtherControl.agency.name,
                id: this.currentOtherControl.agency.id
              })
            }
            this.$q.loading.hide()
          })
          .then(() => {
            if (this.currentOtherControl?.residence?.id) {
              this.selectResidence({
                name: this.currentOtherControl.residence.name,
                id: this.currentOtherControl.residence.id
              })
            }
          })
      }
      const newPath = `${this.$route.path}?tab=${this.selectedTab}`
      if (newPath !== this.$route.fullPath) {
        this.$router.push(newPath)
      }
    },
    agencies() {
      if (this.agencies && this.agencies.length === 1) {
        this.selectAgency(this.agencies[0])
      }
    },
    residences() {
      if (this.residences.length === 1) {
        this.selectResidence(this.residences[0])
      }
    },
    selectedAgencyId(id) {
      const agency = this.agencies.find((a) => a.id === id)
      this.selectAgency(agency)
    },
    selectedResidenceId(id) {
      const residence = this.residences.find((r) => r.id === id)
      this.selectResidence(residence)
    },
    async allAudits() {
      this.zones = await this.reducedZones()
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/attributes',
      allAudits: 'audits/all',
      isLoadingAudits: 'audits/isLoadingAudits',
      currentOtherControl: 'audits/currentOtherControl'
    }),
    selectedAgencyId() {
      return this.selectedAgency?.value
    },
    selectedResidenceId() {
      return this.selectedResidence?.value
    },
    showTabs() {
      return this.company.allowUnplannedAudits && this.isChecklister()
    },
    sortedPlaces() {
      return alphabeticalSort(this.places, (place) => place.name)
    }
  },
  methods: {
    async reducedZones() {
      this.isReducingZones = true
      try {
        const pendingAudits = (
          await this.$DB.tmpReports().find({
            selector: {
              status: {
                $in: ['IN_PROGRESS', 'PENDING', 'DONE']
              }
            }
          })
        ).docs.map((r) => r.audit.id)
        const todos = (
          await this.$patrimoine.auditsCollection().find({
            selector: {
              id: {
                $in: this.allAudits,
                $nin: pendingAudits
              }
            }
          })
        ).docs
        let zones = this.$helpers.removeDuplicates(
          Array.from(todos, (audit) => audit.place.zone),
          'id'
        )
        zones.forEach((zone) => {
          const zoneAudits = todos.filter(
            (audit) => audit.place.zone.id === zone.id
          )
          zone.auditsCount = zoneAudits.length
        })
        return zones.sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.warn('reducedZones', error)
      } finally {
        this.isReducingZones = false
      }
    },
    isChecklister() {
      return this.$helpers
        .decodeToken(this.$store.getters['reporter/token'])
        .roles.includes('checklister')
    },
    sortList(tabOptions) {
      return tabOptions.sort((a, b) => {
        if (a.label === null) {
          return true
        } else {
          return String(a.label).localeCompare(String(b.label), 'fr', {
            ignorePunctuation: true,
            numeric: true,
            sensitivity: 'base'
          })
        }
      })
    },
    selectAgency(agency) {
      this.selectedResidence = { label: '', value: '' }
      if (agency) {
        this.selectedAgency = { label: agency.name, value: agency.id }
        this.$q.loading.show()
        this.residences = []
        this.$SowellProxy
          .loadAgencyResidences(agency.id, true)
          .then((response) => {
            response.data.forEach((residence) => {
              if (residence['has-audits'] === true) {
                this.residences.push({
                  id: residence.id,
                  name: residence.name
                })
              }
            })
            this.residenceSelectOptions = this.sortList(
              this.$helpers.resourcesToFormOptions(this.residences)
            )
            this.$q.loading.hide()
          })
      }
    },
    selectResidence(residence) {
      this.selectedPlaceId = null
      if (residence) {
        this.selectedResidence = { label: residence.name, value: residence.id }
        this.$q.loading.show()
        this.places = []
        this.$SowellProxy
          .loadResidencePlaces(residence.id, true)
          .then((response) => {
            const agency = this.agencies.find(
              (a) => a.id === this.selectedAgencyId
            )
            this.$store.commit('audits/SAVE_SELECTED_RESIDENCE', {
              residence,
              agency
            })
            response.data.map((place) => {
              let isIncludedInPlaces = this.places.some(
                (currentPlace) => currentPlace.id == place.id
              )
              if (place['has-audits'] === true && !isIncludedInPlaces) {
                this.places.push({
                  id: Number(place.id),
                  name: place.name
                })
              }
            })
            this.$q.loading.hide()
          })
      }
    },
    resetAuditsInOtherScopes(excepted = []) {
      switch (excepted) {
        case !excepted.includes('agencies'):
          this.agencies = []
          break
        case !excepted.includes('residences'):
          this.residences = []
          break
        case !excepted.includes('selectedAgencyId'):
          this.selectedAgency = { label: '', value: '' }
          break
        case !excepted.includes('selectedResidenceId'):
          this.selectedResidence = { label: '', value: '' }
          break
      }
    },
    loadAuditsInOtherScopes(place) {
      this.selectedId = place.id
      this.$store.dispatch('audits/loadAuditsInOtherScopes', place).then(() => {
        this.$router.push(
          `/zones/${this.selectedResidenceId}/places/${place.id}?tab=${this.selectedTab}`
        )
      })
    }
  }
}
</script>

<style scoped>
.hide_tabs {
  display: none;
}
.qSelect {
  width: 100%;
}
</style>
